import useConversations from './useConversations';

export interface UseChatUnreadParams {
    tenantId: string | null | undefined;
    myUserId: string | null | undefined;
    enabled?: boolean;
}

/**
 * useChatUnread — the numeric unread count for the sidebar "Chat" badge.
 *
 * WHY A SEPARATE HOOK
 * -------------------
 * The badge lives in the app shell (AuthedApp → AppNav), which is mounted on
 * EVERY route — not just /chat. It therefore needs the unread count without
 * rendering the messenger.
 *
 * The count itself is NOT recomputed here: this hook re-uses `useConversations`
 * verbatim. Because TanStack Query caches by queryKey
 * (`queryKeys.messenger.conversations(tenantId)`), the shell and the open
 * ChatView share ONE cached query — opening /chat does not trigger a second
 * fetch, and a refetch triggered by either surface updates both.
 *
 * This mirrors the spec decision (§4.4): the badge derives from
 * `chat_conversation_members.last_read_at` vs the newest message — never from
 * the notifications/FCM pipeline.
 *
 * NOTE: `enabled` is forwarded so the shell can avoid fetching for users who
 * do not have the chat tab at all (no wasted round-trip).
 */
export function useChatUnread({ tenantId, myUserId, enabled = true }: UseChatUnreadParams) {
    const { totalUnread, isLoading } = useConversations({ tenantId, myUserId, enabled });

    return {
        unreadCount: totalUnread,
        isLoading,
    };
}

export default useChatUnread;
